import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, RefreshCw, TrendingUp, Mail, Eye, MousePointer, AlertTriangle, UserX } from 'lucide-react';
import { getCampaign, getCampaignStats, edgeFunctions } from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDateTime, formatNumber, formatPercent } from '../../lib/format';

export function CampaignStats() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const campaignQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaign(id!),
    enabled: !!id,
  });

  const statsQuery = useQuery({
    queryKey: ['campaign-stats', id],
    queryFn: () => getCampaignStats(id!),
    enabled: !!id,
    refetchInterval: 30_000,
  });

  const refreshMutation = useMutation({
    mutationFn: () => edgeFunctions.refreshCampaignStats(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaign-stats', id] }),
  });

  const campaign = campaignQuery.data;
  const stats = statsQuery.data;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/campaigns')}>
          <ChevronLeft size={16} /> Zurück
        </button>
      </div>

      <PageHeader
        title={campaign?.name ?? 'Kampagnen-Statistik'}
        subtitle={campaign ? `Versendet am ${formatDateTime(campaign.sent_at)}` : ''}
        actions={
          <>
            {campaign && <StatusBadge status={campaign.status} />}
            <button className="btn btn-secondary" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
              <RefreshCw size={14} /> Aktualisieren
            </button>
          </>
        }
      />

      {campaignQuery.isLoading || statsQuery.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="loading-spinner" />
        </div>
      ) : !stats ? (
        <div className="alert alert-info">
          <TrendingUp size={16} />
          <div>Noch keine Statistiken verfügbar. Klicken Sie auf "Aktualisieren", um von Brevo zu laden, oder warten Sie auf den nächsten Webhook.</div>
        </div>
      ) : (
        <>
          <div className="kpi-grid">
            <div className="kpi-card">
              <p className="kpi-label"><Mail size={13} style={{ marginRight: 4, display: 'inline' }} /> Versendet</p>
              <div className="kpi-value">{formatNumber(stats.sent_count)}</div>
              <p className="kpi-hint">{formatNumber(stats.delivered_count)} zugestellt</p>
            </div>
            <div className="kpi-card">
              <p className="kpi-label"><Eye size={13} style={{ marginRight: 4, display: 'inline' }} /> Öffnungen</p>
              <div className="kpi-value">{formatNumber(stats.unique_opens)}</div>
              <p className="kpi-hint">{formatPercent(stats.unique_opens, stats.delivered_count)} Öffnungsrate</p>
            </div>
            <div className="kpi-card">
              <p className="kpi-label"><MousePointer size={13} style={{ marginRight: 4, display: 'inline' }} /> Klicks</p>
              <div className="kpi-value">{formatNumber(stats.unique_clicks)}</div>
              <p className="kpi-hint">{formatPercent(stats.unique_clicks, stats.delivered_count)} Klickrate</p>
            </div>
            <div className="kpi-card">
              <p className="kpi-label"><UserX size={13} style={{ marginRight: 4, display: 'inline' }} /> Abmeldungen</p>
              <div className="kpi-value" style={{ color: 'var(--danger-dark)' }}>{formatNumber(stats.unsubscribed)}</div>
              <p className="kpi-hint">{formatPercent(stats.unsubscribed, stats.delivered_count)} Unsubscribe-Rate</p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="card">
              <h2 className="card-title">Zustellbarkeit</h2>
              <table className="data-table" style={{ border: 'none', marginTop: '0.75rem' }}>
                <tbody>
                  <tr><td>Zugestellt</td><td style={{ textAlign: 'right' }}><strong>{formatNumber(stats.delivered_count)}</strong></td></tr>
                  <tr><td style={{ color: 'var(--warning-dark)' }}>Soft Bounces</td><td style={{ textAlign: 'right' }}>{formatNumber(stats.soft_bounces)}</td></tr>
                  <tr><td style={{ color: 'var(--danger-dark)' }}>Hard Bounces</td><td style={{ textAlign: 'right' }}>{formatNumber(stats.hard_bounces)}</td></tr>
                  <tr><td>Beschwerden</td><td style={{ textAlign: 'right' }}>{formatNumber(stats.complaints)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="card">
              <h2 className="card-title">Engagement</h2>
              <table className="data-table" style={{ border: 'none', marginTop: '0.75rem' }}>
                <tbody>
                  <tr><td>Öffnungen (total)</td><td style={{ textAlign: 'right' }}>{formatNumber(stats.opened_count)}</td></tr>
                  <tr><td>Eindeutige Öffnungen</td><td style={{ textAlign: 'right' }}><strong>{formatNumber(stats.unique_opens)}</strong></td></tr>
                  <tr><td>Klicks (total)</td><td style={{ textAlign: 'right' }}>{formatNumber(stats.clicked_count)}</td></tr>
                  <tr><td>Eindeutige Klicks</td><td style={{ textAlign: 'right' }}><strong>{formatNumber(stats.unique_clicks)}</strong></td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {stats.links_stats && stats.links_stats.length > 0 && (
            <div className="card" style={{ marginTop: '1.5rem' }}>
              <h2 className="card-title">Link-Performance</h2>
              <table className="data-table" style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr><th>URL</th><th style={{ textAlign: 'right' }}>Klicks</th></tr>
                </thead>
                <tbody>
                  {stats.links_stats.slice().sort((a, b) => b.clicks - a.clicks).map((link, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}>{link.url}</td>
                      <td style={{ textAlign: 'right' }}><strong>{formatNumber(link.clicks)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {campaign?.last_error && (
            <div className="alert alert-danger" style={{ marginTop: '1.5rem' }}>
              <AlertTriangle size={16} />
              <div><strong>Letzter Fehler:</strong> {campaign.last_error}</div>
            </div>
          )}

          <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: '1.5rem' }}>
            Letzte Aktualisierung: {formatDateTime(stats.last_updated)}
          </p>
        </>
      )}
    </div>
  );
}
